import { cn } from "@fatbees/ui-utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ComponentPropsWithoutRef, ForwardedRef, forwardRef } from "react";

export const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        mutedLink:
          "underline underline-offset-4 decoration-dotted decoration-neutral-200 hover:decoration-neutral-700",
      },
      size: {
        sm: "h-8 gap-2 [&_svg:not([class*='size-'])]:size-4",
        md: "h-9 gap-3 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 gap-5 [&_svg:not([class*='size-'])]:size-6",
      },
      padding: {
        none: "",
        sm: "",
        md: "",
      },
    },
    compoundVariants: [
      { size: "sm", padding: "sm", className: "px-2" },
      { size: "sm", padding: "md", className: "px-3 has-[>svg]:px-2.5" },
      { size: "md", padding: "sm", className: "px-2 has-[>svg]:px-1.5" },
      { size: "md", padding: "md", className: "px-4 has-[>svg]:px-3" },
      { size: "lg", padding: "sm", className: "px-3 has-[>svg]:px-2" },
      { size: "lg", padding: "md", className: "px-6 has-[>svg]:px-4" },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
      padding: "md",
    },
  },
);

export interface ButtonProps
  extends
    ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef(function Button(
  { className, variant, size, padding, asChild = false, ...props }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Comp = asChild ? Slot : "button";
  const isLink = ["link", "mutedLink"].includes(variant ?? "default");
  const defaultPadding = isLink ? "none" : undefined;

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({
          variant,
          size,
          padding: padding ?? defaultPadding,
          className,
        }),
      )}
      ref={ref}
      {...props}
    />
  );
});
